import React from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle } from 'lucide-react';
import { DataSource, SyncRun } from '../types';

interface ActivityTabProps {
  runs: SyncRun[];
  sources: DataSource[];
}

const ActivityTab: React.FC<ActivityTabProps> = ({ runs, sources }) => {
  const { t } = useTranslation();

  const describe = (run: SyncRun): string => {
    if (run.messageKey) {
      const base = t(run.messageKey) as string;
      return run.messageValue ? `${base}: ${run.messageValue}` : base;
    }
    return t('extInt.activity.imported', {
      created: run.created, updated: run.updated, skipped: run.skipped,
    }) as string;
  };

  return (
    <div className="space-y-6">
      <section>
        <h2 className="text-base font-semibold text-foreground">{t('extInt.activity.heading')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('extInt.activity.subheading')}</p>
      </section>

      {runs.length === 0 ? (
        <p className="rounded-lg border border-border bg-muted/30 p-6 text-sm text-muted-foreground" data-testid="activity-empty">
          {t('extInt.activity.empty')}
        </p>
      ) : (
        <ol className="rounded-lg border border-border bg-card" data-testid="activity-list">
          {runs.map((run) => {
            const source = sources.find((s) => s.id === run.sourceId);
            return (
              <li
                key={run.id}
                className="flex flex-wrap items-start gap-3 border-t border-border px-3 py-3 first:border-t-0"
                data-testid={`activity-row-${run.id}`}
              >
                {run.outcome === 'success' ? (
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" aria-hidden="true" />
                ) : (
                  <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" aria-hidden="true" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-foreground">{describe(run)}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {run.dataType ? `${t(`extInt.dataType.${run.dataType}`)} · ` : ''}
                    {source ? `${source.displayName} · ` : ''}
                    {run.actorName}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground">
                  {new Date(run.occurredOn).toLocaleString()}
                </span>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
};

export default ActivityTab;
